import { FormEvent, useEffect } from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { FormattedMessage, useIntl } from "react-intl"
import { AuthShell } from "@/components/AuthShell"
import { GoogleButton, OrDivider } from "@/components/GoogleButton"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import type { PageProps } from "@/types/inertia"

type SignupProgram = { name: string; slug: string; code_required: boolean }

export default function Signup() {
  const intl = useIntl()
  const { props } = usePage<PageProps<{ google_enabled?: boolean; program: SignupProgram | null; code: string }>>()
  const program = props.program
  const form = useForm({
    email: "",
    password: "",
    timezone: "",
    program: program?.slug ?? "",
    code: props.code ?? "",
  })
  // Programs always ask for a code (required or not); so does any link that arrived with one.
  const showCode = !!program || !!props.code

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz) form.setData("timezone", tz)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/signup")
  }

  const errors = props.errors ?? {}
  const title = intl.formatMessage({ id: "auth.signup.title" })
  const description = intl.formatMessage({ id: "auth.signup.description" })

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AuthShell betaNote>
        <div className="text-center">
          <h2 className="text-2xl">{intl.formatMessage({ id: "auth.signup.heading" })}</h2>
          {program && (
            <p className="mt-2 text-sm">{intl.formatMessage({ id: "auth.signup.signing_up_for" }, { program: program.name })}</p>
          )}
          <p className="mt-2 text-sm text-ink-muted">
            <FormattedMessage
              id="auth.signup.have_account"
              values={{ link: (chunks) => <Link href="/login">{chunks}</Link> }}
            />
          </p>
        </div>

        {props.google_enabled && (
          <div className="mt-6">
            <GoogleButton fields={{ program: form.data.program, code: form.data.code }} />
            <OrDivider />
          </div>
        )}

        <form onSubmit={submit} className={props.google_enabled ? "space-y-4" : "mt-6 space-y-4"}>
          <div className="space-y-2">
            <label htmlFor="email">{intl.formatMessage({ id: "common.email" })}</label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              aria-invalid={!!errors.email}
              value={form.data.email}
              onChange={(e) => form.setData("email", e.target.value)}
            />
            {errors.email && (
              <p className="text-xs text-danger-display">{errors.email}</p>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="password">{intl.formatMessage({ id: "common.password" })}</label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              aria-invalid={!!errors.password}
              value={form.data.password}
              onChange={(e) => form.setData("password", e.target.value)}
            />
            {errors.password && (
              <p className="text-xs text-danger-display">{errors.password}</p>
            )}
          </div>
          {showCode && (
            <div className="space-y-2">
              <label htmlFor="code">{intl.formatMessage({ id: program?.code_required ? "auth.signup.access_code" : "auth.signup.access_code_optional" })}</label>
              <Input
                id="code"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required={program?.code_required}
                aria-invalid={!!errors.code}
                value={form.data.code}
                onChange={(e) => form.setData("code", e.target.value)}
              />
              {errors.code && <p className="text-xs text-danger-display">{errors.code}</p>}
            </div>
          )}
          <Button type="submit" disabled={form.processing}>
            {intl.formatMessage({ id: "auth.signup.submit" })}
          </Button>
        </form>
      </AuthShell>
    </>
  )
}
